import { Box, Flex, HStack, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { DARK_TEXT_LIGHT } from 'constants/dark-tokens'
import { buildPlayerSurface, PLAYER_ACCENT } from 'utils/banner-gradient'

// Player identity banner on the dashboard. Purple for every player regardless of
// which organization they are viewing — the organization's own colour belongs to
// OrgTeamBanner, and tinting this one too would blur whose banner is whose.
//
// Presentational only: it reads no store, so the dashboard decides what a "player"
// is and this stays reusable on any surface that has the same four facts.

/** Accent for the position word and the PLAYING FOR bullets. */
const PLAYER_ACCENT_SOFT = '#C4B5FD'

/**
 * md+: a single 128px bar — portrait, name, then PLAYING FOR in a side column.
 * Phone: the bar cannot hold all three across ~340px without clipping the name and
 * the team list, so it stacks — portrait + name on top, a rule, then PLAYING FOR
 * in two columns — and the height follows the content.
 */
const BANNER_PADDING = 12
const DESKTOP_BANNER_HEIGHT = 128
const PORTRAIT_SIZE = { base: 72, md: DESKTOP_BANNER_HEIGHT - BANNER_PADDING * 2 }

/** Beyond this the list is truncated to a "+N more" row so the banner cannot grow. */
const MAX_VISIBLE_TEAMS = 3

interface IPlayerBannerProps {
  playerName: string
  /** e.g. 'CATCHER · RIGHT / SWITCH' — from athleteProfileToPreview. */
  positionLine?: string
  avatarUrl?: string
  /** Organizations the player is rostered with, in display order. */
  playingFor: string[]
}

/** First letter of up to two name words — stand-in when there is no portrait. */
const playerInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

/**
 * Splits 'CATCHER · RIGHT / SWITCH' into the position (accented) and the rest
 * (white), matching the two-tone treatment in the design. A line without the
 * separator — a sport with no throws/bats, say — renders whole in white rather
 * than guessing where to break it.
 */
const PositionLine = ({ line }: { line: string }) => {
  const separator = line.indexOf('·')

  // The position is branded type — condensed, purple, widely tracked — while the
  // throws/bats half is neutral supporting caps. Same pairing the organization
  // banner uses for "{ORG} · {SPORT}".
  const positionProps = {
    fontFamily: 'display',
    fontWeight: 700,
    fontSize: '9px',
    lineHeight: '14px',
    letterSpacing: '2.5px',
    textTransform: 'uppercase',
    color: PLAYER_ACCENT,
    m: 0
  } as const

  const detailProps = {
    fontFamily: 'body',
    fontWeight: 600,
    fontSize: '10px',
    lineHeight: '12px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    color: DARK_TEXT_LIGHT,
    m: 0
  } as const

  // A line with no separator — a sport that records no throws/bats — renders whole
  // in the neutral style rather than guessing where to split it.
  if (separator === -1) {
    return (
      <Text {...detailProps} noOfLines={1}>
        {line}
      </Text>
    )
  }

  return (
    <HStack spacing="8px" align="center" minW={0}>
      <Text {...positionProps} flexShrink={0}>
        {line.slice(0, separator).trim()}
      </Text>
      <Text {...positionProps} letterSpacing="normal" flexShrink={0}>
        ·
      </Text>
      <Text {...detailProps} noOfLines={1}>
        {line.slice(separator + 1).trim()}
      </Text>
    </HStack>
  )
}

const PlayerBanner = ({ playerName, positionLine, avatarUrl, playingFor }: IPlayerBannerProps) => {
  const surface = buildPlayerSurface()
  const visibleTeams = playingFor.slice(0, MAX_VISIBLE_TEAMS)
  const hiddenCount = playingFor.length - visibleTeams.length

  return (
    <Flex
      {...surface}
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'center' }}
      h={{ base: 'auto', md: `${DESKTOP_BANNER_HEIGHT}px` }}
      p={`${BANNER_PADDING}px`}
      gap={{ base: '12px', md: '8px' }}
      // Width is the caller's call — the design's 780px is one canvas measurement,
      // not a rule that survives a narrower viewport.
      w="100%"
      overflow="hidden"
    >
      <HStack spacing="8px" align="center" flex={{ md: 1 }} minW={0}>
        {avatarUrl ? (
          <Box
            as="img"
            src={avatarUrl}
            alt={playerName}
            w={{ base: `${PORTRAIT_SIZE.base}px`, md: `${PORTRAIT_SIZE.md}px` }}
            h={{ base: `${PORTRAIT_SIZE.base}px`, md: `${PORTRAIT_SIZE.md}px` }}
            borderRadius="12px"
            objectFit="cover"
            flexShrink={0}
          />
        ) : (
          <Box
            w={{ base: `${PORTRAIT_SIZE.base}px`, md: `${PORTRAIT_SIZE.md}px` }}
            h={{ base: `${PORTRAIT_SIZE.base}px`, md: `${PORTRAIT_SIZE.md}px` }}
            borderRadius="12px"
            bg="rgba(168, 85, 247, 0.25)"
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontSize={{ base: '24px', md: '32px' }}
            fontWeight={700}
          >
            {playerInitials(playerName)}
          </Box>
        )}

        <VStack align="stretch" spacing="4px" flex={1} minW={0} pl="8px">
          {positionLine && <PositionLine line={positionLine} />}
          <Text
            fontFamily="display"
            fontWeight={700}
            // Design size on the canvas it was drawn for; the phone row only shares
            // its width with the portrait, so it can afford more than before.
            fontSize={{ base: '28px', md: '48px' }}
            lineHeight={{ base: '30px', md: '48px' }}
            letterSpacing="-0.5px"
            textTransform="uppercase"
            color="white"
            m={0}
            noOfLines={1}
          >
            {playerName}
          </Text>
        </VStack>
      </HStack>

      {visibleTeams.length > 0 && (
        <>
          {/* Horizontal rule on the phone, vertical divider on the bar. */}
          <Box
            w={{ base: 'auto', md: '1px' }}
            h={{ base: '1px', md: 'auto' }}
            alignSelf="stretch"
            bg="rgba(255,255,255,0.18)"
            flexShrink={0}
          />
          <VStack
            align="stretch"
            spacing="4px"
            flexShrink={0}
            pl={{ base: 0, md: '8px' }}
            maxW={{ base: 'none', md: '220px' }}
          >
            <Text fontSize="10px" fontWeight={700} letterSpacing="0.12em" color="white" m={0}>
              PLAYING FOR
            </Text>
            <SimpleGrid columns={{ base: 2, md: 1 }} spacingX="16px" spacingY="4px">
              {visibleTeams.map(team => (
                <HStack key={team} spacing="6px" align="center" minW={0}>
                  <Box w="5px" h="5px" borderRadius="full" bg={PLAYER_ACCENT_SOFT} flexShrink={0} />
                  <Text fontSize="14px" fontWeight={600} lineHeight="22px" color="#F2F7FA" m={0} noOfLines={1}>
                    {team}
                  </Text>
                </HStack>
              ))}
            </SimpleGrid>
            {hiddenCount > 0 && (
              <Text fontSize="12px" color="rgba(255,255,255,0.6)" m={0} pl="11px">
                +{hiddenCount} more
              </Text>
            )}
          </VStack>
        </>
      )}
    </Flex>
  )
}

export default PlayerBanner
